import { CheckInData, ReflectionAnalysis } from "@/types/user";
import { wellnessService } from "@/services/wellnessService";
import { toast } from "sonner";

/**
 * Insert a new check-in via API
 * @param userId The user ID
 * @param checkInData Check-in data to store
 * @returns The inserted data or null if error
 */
export const insertCheckInData = async (
  userId: string,
  checkInData: CheckInData
) => {
  if (!userId) {
    const errorMessage =
      "Authentication error: Please sign in to submit a check-in";
    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  try {
    // ALIGNED WITH mobile: Always use POST endpoint which does upsert based on userId + date
    // This handles both create and update cases, and avoids 404 errors if check-in was deleted
    // Backend POST /wellness/check-ins uses createOrUpdateCheckIn() which upserts by date
    const { id, userId: _, ...checkInPayload } = checkInData;
    const data = await wellnessService.createCheckIn(checkInPayload);
    return data;
  } catch (error) {
    console.error("Failed to insert check-in", error);
    toast.error('Failed to save your check-in');
    throw error;
  }
};

/**
 * Fetch check-ins for a user
 */
export const fetchCheckIns = async (
  userId: string,
  startDate?: string,
  endDate?: string
) => {
  try {
    const response = await wellnessService.getCheckIns({ startDate, endDate });
    // API returns data directly
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Failed to fetch check-ins", error);
    return [];
  }
};

/**
 * Analyze reflection using AI
 */
export const analyzeReflection = async (
  reflection: string
): Promise<ReflectionAnalysis | null> => {
  try {
    const insightsService = await import("@/services/insightsService");
    const data = await insightsService.insightsService.analyzeReflection({ reflection });
    return data || null;
  } catch (error) {
    console.error("Failed to analyze reflection", error);
    return null;
  }
};
